import React from "react";
import { C } from "../lib/colors.js";

// Valid only for partial sweeps: callers must handle p>=1 (full circle) and p<=0 (empty ring)
// themselves, because this function would emit a degenerate arc at p=1.
function pieArcPath(p, r, cx, cy) {
  const a = p * 2 * Math.PI - Math.PI / 2,
    x = cx + r * Math.cos(a),
    y = cy + r * Math.sin(a),
    lg = p > 0.5 ? 1 : 0;
  return `M${cx},${cy} L${cx},${cy - r} A${r},${r} 0 ${lg},1 ${x},${y} Z`;
}

// `bg` is the surface behind the pie, used for the unfilled disc so it blends with that surface.
// `color` and `bg` may be raw hex or a `var(--c-*)` string. A `var()` does not resolve in an SVG
// presentation attribute (`fill="var(...)"`) but does resolve via `style`, so the disc and arc
// colors are applied through `style`.
export function PieIcon({ p, sz = 26, color = C.purple, bg = C.bg }) {
  const pf = Number.isFinite(p) ? p : 0,
    r = sz / 2 - 2,
    cx = sz / 2,
    cy = sz / 2;
  if (pf >= 1)
    return (
      <svg width={sz} height={sz} style={{ display: "block" }}>
        <circle cx={cx} cy={cy} r={r} style={{ fill: color }} opacity=".85" />
        <circle cx={cx} cy={cy} r={r} fill="none" style={{ stroke: color }} strokeWidth="1.5" />
      </svg>
    );
  if (pf <= 0)
    return (
      <svg width={sz} height={sz} style={{ display: "block" }}>
        <circle cx={cx} cy={cy} r={r} style={{ fill: bg, stroke: color }} strokeWidth="1.5" />
      </svg>
    );
  return (
    <svg width={sz} height={sz} style={{ display: "block" }}>
      <circle cx={cx} cy={cy} r={r} style={{ fill: bg, stroke: color }} strokeWidth="1.5" />
      <path d={pieArcPath(pf, r, cx, cy)} style={{ fill: color }} opacity=".85" />
    </svg>
  );
}

// The lightbulb is the conventional marker for tour/tip content.
export function LightbulbIcon({ size = 12, color, style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      // `stroke` via style (inherited by the child paths), not a presentation
      // attribute, so a `var(--c-*)` color from a caller resolves. See PieIcon.
      style={{ display: "block", flexShrink: 0, stroke: color, ...style }}
    >
      <line x1="9" y1="18" x2="15" y2="18" />
      <line x1="10" y1="22" x2="14" y2="22" />
      <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V18h6v-1.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" />
    </svg>
  );
}

export function ResolutionIcon({ p, sz = 30, color = C.purple, bg = C.bg, pulseDur = "4s" }) {
  const r = sz / 2 - 2,
    ri = r - 4,
    cx = sz / 2,
    cy = sz / 2;
  if (p >= 1)
    return (
      <svg width={sz} height={sz} style={{ display: "block" }}>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth="4"
          style={{ stroke: color, animation: `resPulse ${pulseDur} ease-in-out infinite` }}
        />
        <circle cx={cx} cy={cy} r={ri} style={{ fill: color }} opacity=".85" />
      </svg>
    );
  if (p <= 0)
    return (
      <svg width={sz} height={sz} style={{ display: "block" }}>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          strokeWidth="4"
          style={{ stroke: color, animation: `resPulse ${pulseDur} ease-in-out infinite` }}
        />
        <circle cx={cx} cy={cy} r={ri} style={{ fill: bg, stroke: color }} strokeWidth="1.5" />
      </svg>
    );
  return (
    <svg width={sz} height={sz} style={{ display: "block" }}>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        strokeWidth="4"
        style={{ stroke: color, animation: `resPulse ${pulseDur} ease-in-out infinite` }}
      />
      <circle cx={cx} cy={cy} r={ri} style={{ fill: bg, stroke: color }} strokeWidth="1.5" />
      <path d={pieArcPath(p, ri, cx, cy)} style={{ fill: color }} opacity=".85" />
    </svg>
  );
}

export function EyeIcon({ on, size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" fill={on ? "currentColor" : "none"} />
    </svg>
  );
}
// Used for error chrome. The icon is decorative because the adjacent "Error" label is the
// accessible signal, so it is aria-hidden.
export function AlertIcon({ size = 11 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ display: "block" }}
    >
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}
export function BellIcon({ size = 10 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}
export function SlidersIcon({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      style={{ display: "block" }}
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="9" cy="6" r="2.2" fill="currentColor" />
      <circle cx="15" cy="12" r="2.2" fill="currentColor" />
      <circle cx="8" cy="18" r="2.2" fill="currentColor" />
    </svg>
  );
}
// The fill is the brand lavender, not `currentColor`, so the mark keeps its brand color under
// the colorless palette. `color` overrides it where the mark is furniture rather than branding.
export function SoothIcon({ size = 11, color = "#D783FF" }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        display: "inline-block",
        background: color,
        WebkitMaskImage: "url(/logo.svg)",
        maskImage: "url(/logo.svg)",
        WebkitMaskSize: "contain",
        maskSize: "contain",
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskPosition: "center",
        maskPosition: "center",
        verticalAlign: "middle",
        flexShrink: 0,
      }}
    />
  );
}
export function BackIcon({ size = 13 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
export function TrashIcon({ size = 12 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

/**
 * A section's open/close arrow: the Signal Library's filled triangle, pointing right when closed
 * and turning down when open, with the same animation.
 */
/** In a disclosure's place when there is nothing more to show: a square says it does not open. */
export function DisclosureSquare({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      <rect x="7" y="7" width="10" height="10" />
    </svg>
  );
}

export function DisclosureTriangle({ open, size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{
        transform: `rotate(${open ? 90 : 0}deg)`,
        transition: "transform 0.3s ease",
        display: "block",
        flexShrink: 0,
      }}
    >
      <polygon points="6,4 6,20 20,12" />
    </svg>
  );
}

export function Triskelion({ size = 48, color = C.purple }) {
  const spiral = (rot) => {
    const cx = 50,
      cy = 50;
    const pts = [];
    for (let t = 0; t <= 540; t += 10) {
      const a = ((t + rot) * Math.PI) / 180;
      const rad = 4 + t * 0.07;
      pts.push([cx + rad * Math.cos(a), cy + rad * Math.sin(a)]);
    }
    const inner = [],
      outer = [];
    for (let i = 0; i < pts.length; i++) {
      const thick = 1.8 + i * 0.06;
      const dx = i < pts.length - 1 ? pts[i + 1][0] - pts[i][0] : pts[i][0] - pts[i - 1][0];
      const dy = i < pts.length - 1 ? pts[i + 1][1] - pts[i][1] : pts[i][1] - pts[i - 1][1];
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = (-dy / len) * thick,
        ny = (dx / len) * thick;
      inner.push([pts[i][0] + nx, pts[i][1] + ny]);
      outer.push([pts[i][0] - nx, pts[i][1] - ny]);
    }
    let d = `M${inner[0][0].toFixed(1)},${inner[0][1].toFixed(1)}`;
    for (let i = 1; i < inner.length; i++)
      d += ` L${inner[i][0].toFixed(1)},${inner[i][1].toFixed(1)}`;
    for (let i = outer.length - 1; i >= 0; i--)
      d += ` L${outer[i][0].toFixed(1)},${outer[i][1].toFixed(1)}`;
    d += " Z";
    return d;
  };
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <g style={{ transformOrigin: "50px 50px", animation: "spin 2s linear infinite" }}>
        <circle cx="50" cy="50" r="5" style={{ fill: color }} />
        {[0, 120, 240].map((a) => (
          <path key={a} d={spiral(a)} style={{ fill: color }} opacity=".85" />
        ))}
      </g>
    </svg>
  );
}

// The arrow direction encodes the sharing: out (↗) for "I am sharing this", in (↙) for
// "this was shared with me". Both badges are the same grey, so the arrow must be the heavy,
// large half to carry that distinction. The 24x18 viewBox fills a wide badge.
export function CollabIcon({ size = 22, direction = "out" }) {
  const out = direction === "out";
  return (
    <svg
      width={size}
      height={(size * 18) / 24}
      viewBox="0 0 24 18"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      {/* The figures use a lighter stroke than the arrow so they read as context and do not
        compete with the meaning-bearing arrow. */}
      <g strokeWidth="1.4">
        <circle cx="6" cy="9.6" r="1.9" />
        <path d="M2.8 15.6c0-1.8 1.4-2.9 3.2-2.9s3.2 1.1 3.2 2.9" />
        <path d="M10.2 8a1.8 1.8 0 0 1 0 3.2" />
        <path d="M11.2 13.1c1.2.4 1.9 1.2 1.9 2.5" />
      </g>
      {/* The arrow's lower-left end must stay clear of the second figure's shoulder (about
        13.2, 14.8) to leave a visible gap while still reading as a diagonal. */}
      <g strokeWidth="1.9">
        {out ? (
          <>
            <path d="M15.6 12.4 21.6 6.4" />
            <path d="M17.4 6.4h4.2v4.2" />
          </>
        ) : (
          <>
            <path d="M21.6 6.4 15.6 12.4" />
            <path d="M19.8 12.4h-4.2V8.2" />
          </>
        )}
      </g>
    </svg>
  );
}

// The settled mark: a resolved forecast shows this where a live one shows its
// percentage. Drawn rather than a "✓" glyph so its weight and size are ours and
// not the system font's — it is deliberately large, because "this one is decided"
// is the loudest thing a settled card has to say.
export function CheckIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      // The weight is fixed here rather than scaled with `size` because the stroke makes the
      // glyph read as a mark; enlarging the box alone would thin it.
      strokeWidth="3.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <path d="M4 12.5 9.5 18 20 6.5" />
    </svg>
  );
}

// BarsIcon and ChevronIcon use a 2.4 stroke because few, short strokes read thin at 2. The
// settings rows tint the icons via the wrapper's color, so greying works by inheritance.
export function CalendarIcon({ size = 17 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="8" y1="3" x2="8" y2="6" />
      <line x1="16" y1="3" x2="16" y2="6" />
    </svg>
  );
}
// Represents the run-depth row (Quick vs Deep). Layers were chosen over a gauge because the
// distinction is research depth, not speed.
export function LayersIcon({ size = 17 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <ellipse cx="12" cy="6" rx="8" ry="3.2" />
      <path d="M4 6v6c0 1.8 3.6 3.2 8 3.2s8-1.4 8-3.2V6" />
      <path d="M4 12v6c0 1.8 3.6 3.2 8 3.2s8-1.4 8-3.2v-6" />
    </svg>
  );
}
// Used for the alert-threshold row. Deliberately not BellIcon, which means a notification
// happened, whereas this row sets the magnitude of a move.
export function BarsIcon({ size = 17 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      style={{ display: "block" }}
    >
      <line x1="5" y1="14" x2="5" y2="20" />
      <line x1="12" y1="4" x2="12" y2="20" />
      <line x1="19" y1="10" x2="19" y2="20" />
    </svg>
  );
}
export function MailIcon({ size = 17 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
    >
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}
export function ChevronIcon({ size = 13, dir = "right" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        display: "block",
        transform: dir === "down" ? "rotate(90deg)" : "none",
        transition: "transform 0.18s",
      }}
    >
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}
